import { useHasAccess } from "@features/dashboard/hooks/useHasAccess";
import { useRecommendationFrontendContext } from "@features/dashboard/hooks/useRecommendationFrontendContext";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { RecommendationContext } from "@memorang/types";
import { useMemo } from "react";
import type { RecommendationFrontendContext } from "../adapters/frontend-context";
import { adaptContextResultForThreeAxisUi } from "../adapters/ui-adapter";
import { selectDurationRecommendations } from "../application/build-duration-options";
import { selectStudyRecommendations } from "../application/select-study-recommendations";
import { parseRecommendationContext } from "../contract/recommendation-context-schema";
import type {
	AxisFilters,
	ContextRecommendedSession,
} from "../contract/recommendation-context-types";
import type { RecommendationResult } from "../types";

export type HydratedRecommendationFallbackReason =
	| "MISSING_CONTEXT"
	| "INVALID_CONTEXT"
	| "UNSUPPORTED_SCHEMA"
	| "MISSING_TARGET"
	| "NO_MATCHING_CELLS"
	| "NO_AVAILABLE_ITEMS";

export type HydratedRecommendationState =
	| { status: "DISABLED" }
	| { status: "LOADING" }
	| {
			status: "READY";
			sessions: ContextRecommendedSession[];
			presentation: RecommendationResult;
			context: RecommendationContext;
			frontend: RecommendationFrontendContext;
	  }
	| { status: "FALLBACK"; reason: HydratedRecommendationFallbackReason };

const filtersForValues = (
	context: RecommendationContext,
	valueIds: number[],
): AxisFilters | null => {
	const filters: AxisFilters = {};
	for (const valueId of valueIds) {
		const matchingAxes = context.axes.filter((axis) =>
			axis.values.some(({ id }) => id === valueId),
		);
		if (matchingAxes.length !== 1) {
			return null;
		}
		const axisId = matchingAxes[0].id;
		filters[axisId] = [...(filters[axisId] ?? []), valueId];
	}
	return filters;
};

type ResolveHydratedStateInput = {
	rawContext: unknown;
	frontend: RecommendationFrontendContext;
	durationOptions: boolean;
	sessionCount: number;
	itemsPerSession?: number;
	durationMinutes?: 5 | 10 | 20;
	onePerTrack?: boolean;
	filterValueIds: number[];
};

const DEFAULT_ITEMS_PER_SESSION = 10;
const NO_FILTER_VALUE_IDS: number[] = [];

const selectFromValidatedContext = ({
	context,
	frontend,
	durationOptions,
	sessionCount,
	itemsPerSession,
	durationMinutes,
	onePerTrack,
	filterValueIds,
}: Omit<ResolveHydratedStateInput, "rawContext"> & {
	context: RecommendationContext;
}): HydratedRecommendationState => {
	const progression = context.axes.find(({ role }) => role === "PROGRESSION");
	if (!progression) {
		return { status: "FALLBACK", reason: "INVALID_CONTEXT" };
	}
	const filters = filtersForValues(context, filterValueIds);
	if (filters === null) {
		return { status: "FALLBACK", reason: "NO_MATCHING_CELLS" };
	}
	const trackAxes = context.axes.filter(({ role }) => role === "TRACK");
	const input = {
		context,
		target: {
			axisId: progression.id,
			valueId: frontend.targetProficiencyId as number,
		},
		filters,
		preferences:
			trackAxes.length === 1
				? { [trackAxes[0].id]: frontend.preferredSkillIds }
				: undefined,
	};
	const resolvedItemsPerSession =
		!durationOptions &&
		itemsPerSession === undefined &&
		durationMinutes === undefined
			? DEFAULT_ITEMS_PER_SESSION
			: itemsPerSession;
	const result = durationOptions
		? selectDurationRecommendations(input)
		: selectStudyRecommendations({
				...input,
				sessionCount,
				itemsPerSession: resolvedItemsPerSession,
				durationMinutes,
				onePerTrack,
			});
	if (result.status !== "READY") {
		return { status: "FALLBACK", reason: result.reason };
	}
	return {
		status: "READY",
		sessions: result.sessions,
		presentation: adaptContextResultForThreeAxisUi({ context, result }),
		context,
		frontend,
	};
};

const resolveHydratedState = ({
	rawContext,
	frontend,
	...selection
}: ResolveHydratedStateInput): HydratedRecommendationState => {
	if (!rawContext) {
		return { status: "FALLBACK", reason: "MISSING_CONTEXT" };
	}
	if (
		typeof rawContext !== "object" ||
		!("schemaVersion" in rawContext) ||
		rawContext.schemaVersion !== 2
	) {
		return { status: "FALLBACK", reason: "UNSUPPORTED_SCHEMA" };
	}
	if (frontend.targetProficiencyId === undefined) {
		return { status: "FALLBACK", reason: "MISSING_TARGET" };
	}
	try {
		return selectFromValidatedContext({
			context: parseRecommendationContext(rawContext),
			frontend,
			...selection,
		});
	} catch {
		return { status: "FALLBACK", reason: "INVALID_CONTEXT" };
	}
};

export const useHydratedStudyRecommendations = ({
	durationOptions = false,
	sessionCount = 3,
	itemsPerSession,
	durationMinutes,
	onePerTrack = false,
	filterValueIds = NO_FILTER_VALUE_IDS,
	additionalExamCollectionIds,
}: {
	durationOptions?: boolean;
	sessionCount?: number;
	itemsPerSession?: number;
	durationMinutes?: 5 | 10 | 20;
	onePerTrack?: boolean;
	filterValueIds?: number[];
	additionalExamCollectionIds?: number[];
} = {}): HydratedRecommendationState => {
	const distribution = useMixedItemDist({
		action: "GET_STUDYPACK_DISTRIBUTION",
	});
	const { isLoading: accessLoading } = useHasAccess();
	const frontend = useRecommendationFrontendContext({
		studyPackData: distribution.data,
		additionalExamCollectionIds,
	});

	return useMemo(() => {
		if (!frontend.enabled) {
			return { status: "DISABLED" };
		}
		if (distribution.isLoading || accessLoading) {
			return { status: "LOADING" };
		}
		return resolveHydratedState({
			rawContext: distribution.data?.recommendationContext,
			frontend,
			durationOptions,
			sessionCount,
			itemsPerSession,
			durationMinutes,
			onePerTrack,
			filterValueIds,
		});
	}, [
		accessLoading,
		distribution.data,
		distribution.isLoading,
		durationMinutes,
		durationOptions,
		filterValueIds,
		frontend,
		itemsPerSession,
		onePerTrack,
		sessionCount,
	]);
};

export const selectScopedStudyPresentation = ({
	state,
	filterValueIds,
	sessionCount,
}: {
	state: HydratedRecommendationState;
	filterValueIds: number[];
	sessionCount: number;
}): RecommendationResult | undefined => {
	if (state.status !== "READY") {
		return undefined;
	}
	const { context, frontend } = state;
	const progression = context.axes.find(({ role }) => role === "PROGRESSION");
	if (!progression || frontend.targetProficiencyId === undefined) {
		return undefined;
	}
	const filters = filtersForValues(context, filterValueIds);
	if (filters === null) {
		return undefined;
	}
	const trackAxes = context.axes.filter(({ role }) => role === "TRACK");
	const result = selectStudyRecommendations({
		context,
		target: {
			axisId: progression.id,
			valueId: frontend.targetProficiencyId as number,
		},
		filters,
		preferences:
			trackAxes.length === 1
				? { [trackAxes[0].id]: frontend.preferredSkillIds }
				: undefined,
		sessionCount,
		durationMinutes: 10,
	});
	if (result.status !== "READY") {
		return undefined;
	}
	return adaptContextResultForThreeAxisUi({ context, result });
};

export const hasScopedStudyRecommendation = (
	state: HydratedRecommendationState,
	filterValueIds: number[],
): boolean => {
	const presentation = selectScopedStudyPresentation({
		state,
		filterValueIds,
		sessionCount: 1,
	});
	return presentation?.status === "READY" && presentation.sessions.length > 0;
};

/**
 * Chip predicate: FALLBACK is optimistically true — the legacy lane will
 * probably serve the dialog, and an empty dialog is an accepted worst case.
 */
export const hasLikelyPracticeRecommendation = (
	state: HydratedRecommendationState,
	filterValueIds: number[],
): boolean =>
	state.status === "FALLBACK"
		? true
		: hasScopedStudyRecommendation(state, filterValueIds);
